/** Confirm the in-place takeover. The shipped endpoint takes no request body. */
import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { errorCopy, isOutcomeUnknown } from '@/api/errors';
import type { ResellerDto } from '@/api/tmm/types';

interface Props {
  open: boolean;
  from: ResellerDto;
  onClose: () => void;
  onDone: () => void;
  onOutcomeUnknown: () => void;
}

export function DeactivateResellerDialog({ open, ...props }: Props) {
  const { t } = useCopy();
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onClose={busy ? undefined : props.onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('resellers.deactivate.title', 'Deactivate partner')}</DialogTitle>
      {open ? <DeactivateForm {...props} busy={busy} setBusy={setBusy} /> : null}
    </Dialog>
  );
}

function DeactivateForm({
  from,
  onClose,
  onDone,
  onOutcomeUnknown,
  busy,
  setBusy,
}: Omit<Props, 'open'> & { busy: boolean; setBusy: (value: boolean) => void }) {
  const { t } = useCopy();
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await adminApi.deactivateReseller(from.id);
      onDone();
      onClose();
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        onOutcomeUnknown();
        onClose();
        return;
      }
      setError(
        errorCopy(err, t, t('resellers.deactivate.error', 'Could not deactivate the partner.')),
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <DialogContent>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <Typography>
          {t('resellers.deactivate.confirm', 'Deactivate {{from}}?', { from: from.name })}
        </Typography>
        <Typography sx={{ mt: 2 }}>
          {t(
            'resellers.deactivate.consequence',
            "Deactivating suspends this partner's administrators and makes the platform manage it. Its tenant keeps selling. Settlements are retained by the platform (nothing is paid out) until you reactivate.",
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('resellers.cancel', 'Cancel')}
        </Button>
        <SubmitButton color="error" busy={busy} onClick={() => void submit()}>
          {t('resellers.deactivate.submit', 'Deactivate')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
